import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PhotoIcon from '@mui/icons-material/Photo';
import jsPDF from 'jspdf';
import { IconButton, Card, Grid, Chip } from '@mui/joy';
import {
  Box,
  Typography,
  Select,
  Option,
  Modal,
  ModalDialog,
  ModalClose,
  Button,
  FormControl,
  FormLabel,
  Input,
  Autocomplete,
  Tabs,
  TabList,
  Tab,
  TabPanel
} from '@mui/joy';
import EnhancedButton from '../components/EnhancedButton';
import EnhancedInput from '../components/EnhancedInput';
import { supabase } from '../supabaseClient';
import { AuthContext } from '../context/AuthContext.jsx';
import PageLayout from '../components/PageLayout';
import StyledTable from '../components/StyledTable';
import SurveyAnalytics from '../components/SurveyAnalytics';
import FileUpload from '../components/FileUpload';
import FileManager from '../components/FileManager';
import { FileUploadService } from '../utils/fileUploadService';

const SurveyTracker = () => {
  const { user } = useContext(AuthContext);
  const [surveyProjects, setSurveyProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [surveyTypes, setSurveyTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [formData, setFormData] = useState({
    project_id: '',
    county: '',
    date_delivered: '',
    status: 'Not Started',
    assigned_user_id: '',
    due_date: '',
    priority: null,
    survey_types: [],
    locations: [{ section: '', township: '', range: '' }]
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [countyFilter, setCountyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [assignedUserFilter, setAssignedUserFilter] = useState('');
  const [exportingPDF, setExportingPDF] = useState(false);
  const [quickFilters, setQuickFilters] = useState({
    hasFiles: false,
    hasComments: false,
    deliveredThisMonth: false,
    isOverdue: false,
    isHighPriority: false
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState(0);
  const [projectFiles, setProjectFiles] = useState({ documents: [], photos: [], totalFiles: 0 });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  const fetchData = async () => {
    // Fetch survey projects with locations, types, comments, and linked project info
    const { data: surveyProjectData, error: surveyError } = await supabase
      .from('survey_projects')
      .select(`
        *,
        projects (
          id,
          name,
          project_number
        ),
        assigned_user:users!assigned_user_id (
          id,
          display_name,
          email
        ),
        survey_locations (
          id,
          section,
          township,
          range
        ),
        survey_project_types (
          survey_types (
            id,
            type_name
          )
        ),
        survey_comments (
          id,
          comment_text,
          created_at,
          user_id
        )
      `)
      .order('created_at', { ascending: false });

    if (surveyError) {
      console.error('Error fetching survey projects:', surveyError);
    }

    // Fetch all projects for selection dropdown
    const { data: allProjectsData, error: projectsError } = await supabase
      .from('projects')
      .select('id, name, project_number')
      .order('name');
    
    if (projectsError) {
      console.error('Error fetching projects:', projectsError);
    }

    // Fetch survey types
    const { data: typeData } = await supabase
      .from('survey_types')
      .select('*')
      .eq('is_active', true)
      .order('type_name');

    // Fetch users from Survey department only (for assigned surveyor dropdown)
    const { data: userData } = await supabase
      .from('users')
      .select('id, display_name, email, department')
      .eq('department', 'Survey')
      .order('display_name');

    if (surveyProjectData) {
      // Get user data for comments if there are any
      const userIds = [...new Set(
        surveyProjectData.flatMap(p => 
          p.survey_comments?.map(c => c.user_id).filter(Boolean) || []
        )
      )];
      
      let userMap = {};
      if (userIds.length > 0) {
        const { data: usersData } = await supabase
          .from('users')
          .select('id, display_name, email')
          .in('id', userIds);
        
        userMap = (usersData || []).reduce((acc, user) => {
          acc[user.id] = user;
          return acc;
        }, {});
      }

      // Get file counts for each project
      const projectIds = surveyProjectData.map(p => p.id);
      let fileCounts = {};
      
      if (projectIds.length > 0) {
        // Fetch document counts
        const { data: documentCounts } = await supabase
          .from('survey_documents')
          .select('survey_project_id')
          .in('survey_project_id', projectIds);
        
        // Fetch photo counts
        const { data: photoCounts } = await supabase
          .from('survey_photos')
          .select('survey_project_id')
          .in('survey_project_id', projectIds);
        
        // Process counts
        projectIds.forEach(id => {
          fileCounts[id] = {
            documents: documentCounts?.filter(d => d.survey_project_id === id).length || 0,
            photos: photoCounts?.filter(p => p.survey_project_id === id).length || 0
          };
        });
      }

      const enriched = surveyProjectData.map(p => ({
        ...p,
        project_name: p.projects?.name || 'Unknown Project',
        project_number: p.projects?.project_number || '',
        assigned_user_name: p.assigned_user?.display_name || p.assigned_user?.email || null,
        survey_types: p.survey_project_types?.map(spt => spt.survey_types) || [],
        locations_text: p.survey_locations?.map(l => 
          `${l.section}-${l.township}-${l.range}`
        ).join(', ') || '',
        survey_comments: p.survey_comments?.map(c => ({
          ...c,
          user_name: userMap[c.user_id]?.display_name || userMap[c.user_id]?.email || 'Unknown User'
        })) || [],
        comment_count: p.survey_comments?.length || 0,
        file_counts: fileCounts[p.id] || { documents: 0, photos: 0 }
      }));
      setSurveyProjects(enriched);
    }

    if (allProjectsData) {
      setAllProjects(allProjectsData);
    }
    if (typeData) setSurveyTypes(typeData);
    if (userData) setUsers(userData);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (project) => {
    setFormData({
      id: project.id,
      project_id: project.project_id,
      county: project.county,
      date_delivered: project.date_delivered || '',
      status: project.status || 'Not Started',
      assigned_user_id: project.assigned_user_id || '',
      due_date: project.due_date || '',
      priority: project.priority || null,
      survey_types: project.survey_types.map(t => t.id),
      locations: project.survey_locations?.length > 0 
        ? project.survey_locations 
        : [{ section: '', township: '', range: '' }]
    });
    setActiveTab(0); // Reset to first tab
    setOpen(true);
  };

  const handleViewFiles = (project) => {
    setFormData({
      id: project.id,
      project_id: project.project_id,
      county: project.county,
      date_delivered: project.date_delivered || '',
      status: project.status || 'Not Started',
      assigned_user_id: project.assigned_user_id || '',
      due_date: project.due_date || '',
      priority: project.priority || null,
      survey_types: project.survey_types.map(t => t.id),
      locations: project.survey_locations?.length > 0 
        ? project.survey_locations 
        : [{ section: '', township: '', range: '' }]
    });
    setActiveTab(1); // Switch to Files tab
    setOpen(true);
  };

  const handleFilesUploaded = (uploadedFiles) => {
    console.log('Files uploaded:', uploadedFiles);
    // Files will be refreshed by FileManager component
  };

  const handleFilesChanged = (files) => {
    setProjectFiles(files);
  };

  const handleDelete = async (project) => {
    if (window.confirm(`Delete project "${project.project_name}"?`)) {
      await supabase.from('survey_projects').delete().eq('id', project.id);
      fetchData();
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        project_id: formData.project_id,
        county: formData.county,
        date_delivered: formData.date_delivered || null,
        status: formData.status,
        assigned_user_id: formData.assigned_user_id || null,
        due_date: formData.due_date || null,
        priority: formData.priority,
        created_by: user.id,
        updated_by: user.id
      };

      let projectId;
      if (formData.id) {
        const { error } = await supabase.from('survey_projects').update(payload).eq('id', formData.id);
        if (error) throw error;
        projectId = formData.id;
      } else {
        const { data, error } = await supabase.from('survey_projects').insert([payload]).select();
        if (error) throw error;
        projectId = data[0].id;
      }

    // Handle locations
    if (formData.id) {
      await supabase.from('survey_locations').delete().eq('project_id', formData.id);
    }
    
    const locationData = formData.locations
      .filter(l => l.section || l.township || l.range)
      .map(l => ({
        project_id: projectId,
        section: l.section,
        township: l.township,
        range: l.range
      }));
    
    if (locationData.length > 0) {
      await supabase.from('survey_locations').insert(locationData);
    }

    // Handle survey types
    if (formData.id) {
      await supabase.from('survey_project_types').delete().eq('project_id', formData.id);
    }
    
    const typeData = formData.survey_types.map(typeId => ({
      project_id: projectId,
      survey_type_id: typeId
    }));
    
    if (typeData.length > 0) {
      await supabase.from('survey_project_types').insert(typeData);
    }

    // Handle pending files for new projects
    if (!formData.id && pendingFiles.length > 0) {
      try {
        await FileUploadService.uploadMultipleFiles(
          pendingFiles,
          projectId,
          user.id
        );
      } catch (fileError) {
        console.warn('Some files failed to upload:', fileError);
        // Don't fail the entire operation if file upload fails
      }
    }

      setOpen(false);
      setActiveTab(0); // Reset to first tab
      setFormData({
        project_id: '',
        county: '',
        date_delivered: '',
        status: 'Not Started',
        assigned_user_id: '',
        due_date: '',
        priority: null,
        survey_types: [],
        locations: [{ section: '', township: '', range: '' }]
      });
      setPendingFiles([]);
      fetchData();
    } catch (error) {
      console.error('Error saving survey project:', error);
      alert('Error saving survey project: ' + error.message);
    }
  };

  const handleViewComments = async (project) => {
    setSelectedProject(project);
    setComments(project.survey_comments || []);
    setCommentModalOpen(true);
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    const { error } = await supabase
      .from('survey_comments')
      .insert([{
        project_id: selectedProject.id,
        user_id: user.id,
        comment_text: newComment
      }]);

    if (!error) {
      setNewComment('');
      fetchData();
      // Refresh comments
      const project = surveyProjects.find(p => p.id === selectedProject.id);
      if (project) {
        setComments(project.survey_comments || []);
      }
    }
  };

  const addLocation = () => {
    setFormData({
      ...formData,
      locations: [...formData.locations, { section: '', township: '', range: '' }]
    });
  };

  const removeLocation = (index) => {
    setFormData({
      ...formData,
      locations: formData.locations.filter((_, i) => i !== index)
    });
  };

  const updateLocation = (index, field, value) => {
    const newLocations = [...formData.locations];
    newLocations[index][field] = value;
    setFormData({ ...formData, locations: newLocations });
  };

  const filteredProjects = surveyProjects.filter(p => {
    const matchesSearch = !searchTerm || (
      p.project_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.county?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.locations_text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.assigned_user_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    const matchesCounty = !countyFilter || p.county === countyFilter;
    const matchesStatus = !statusFilter || p.status === statusFilter;
    const matchesPriority = !priorityFilter || (
      priorityFilter === 'null' ? p.priority === null : p.priority === parseInt(priorityFilter)
    );
    const matchesAssignedUser = !assignedUserFilter || (
      assignedUserFilter === 'unassigned' ? !p.assigned_user_id : p.assigned_user_id === assignedUserFilter
    );
    
    // Quick filter logic
    const hasFiles = (p.file_counts?.documents || 0) + (p.file_counts?.photos || 0) > 0;
    const hasComments = p.comment_count > 0;
    const now = new Date();
    const isDeliveredThisMonth = p.date_delivered && (() => {
      const deliveredDate = new Date(p.date_delivered);
      return deliveredDate.getMonth() === now.getMonth() && 
             deliveredDate.getFullYear() === now.getFullYear();
    })();
    const isOverdue = p.due_date && new Date(p.due_date) < now && p.status !== 'Complete';
    const isHighPriority = p.priority === 1;
    
    const matchesQuickFilters = 
      (!quickFilters.hasFiles || hasFiles) &&
      (!quickFilters.hasComments || hasComments) &&
      (!quickFilters.deliveredThisMonth || isDeliveredThisMonth) &&
      (!quickFilters.isOverdue || isOverdue) &&
      (!quickFilters.isHighPriority || isHighPriority);
    
    return matchesSearch && matchesCounty && matchesStatus && matchesPriority && matchesAssignedUser && matchesQuickFilters;
  });

  const onSortChange = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  let sortedProjects = [...filteredProjects];
  if (sortKey) {
    sortedProjects.sort((a, b) => {
      let aValue = a[sortKey];
      let bValue = b[sortKey];
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Group projects by county
  const groupProjectsByCounty = (projects) => {
    const grouped = projects.reduce((acc, project) => {
      const county = project.county || 'Unknown County';
      if (!acc[county]) {
        acc[county] = [];
      }
      acc[county].push(project);
      return acc;
    }, {});
    
    // Sort counties alphabetically
    return Object.keys(grouped)
      .sort()
      .reduce((acc, county) => {
        acc[county] = grouped[county];
        return acc;
      }, {});
  };

  // Pagination calculations - Updated for county grouping
  const totalItems = sortedProjects.length;
  const projectsPerCounty = 10; // Show 10 projects per county initially
  const maxCountiesPerPage = 3; // Show 3 counties per page
  
  // Group all sorted projects by county first
  const allGroupedProjects = groupProjectsByCounty(sortedProjects);
  const countiesArray = Object.entries(allGroupedProjects);
  
  // Paginate counties
  const totalCounties = countiesArray.length;
  const totalPages = Math.ceil(totalCounties / maxCountiesPerPage);
  const startCountyIndex = (currentPage - 1) * maxCountiesPerPage;
  const endCountyIndex = startCountyIndex + maxCountiesPerPage;
  const paginatedCounties = countiesArray.slice(startCountyIndex, endCountyIndex);
  
  // State for expanded counties (to show more than 10 projects)
  const [expandedCounties, setExpandedCounties] = React.useState(new Set());

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, countyFilter, statusFilter, priorityFilter, assignedUserFilter, quickFilters]);

  const toggleQuickFilter = (filterKey) => {
    setQuickFilters(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey]
    }));
  };

  const getActiveFilterCount = () => {
    return Object.values(quickFilters).filter(Boolean).length;
  };

  // Function to handle expanding counties to show all projects
  const toggleCountyExpansion = (county) => {
    setExpandedCounties(prev => {
      const newSet = new Set(prev);
      if (newSet.has(county)) {
        newSet.delete(county);
      } else {
        newSet.add(county);
      }
      return newSet;
    });
  };

  // Final grouped projects with expansion logic
  const finalGroupedProjects = paginatedCounties.reduce((acc, [county, projects]) => {
    const isExpanded = expandedCounties.has(county);
    acc[county] = isExpanded ? projects : projects.slice(0, projectsPerCounty);
    return acc;
  }, {});

  // Priority configuration helper
  const getPriorityConfig = (priority) => {
    switch (priority) {
      case 1:
        return { color: 'danger', icon: 'priority_high', label: 'High Priority' };
      case 2:
        return { color: 'warning', icon: 'drag_handle', label: 'Medium Priority' };
      case 3:
        return { color: 'success', icon: 'low_priority', label: 'Low Priority' };
      default:
        return { color: 'neutral', icon: 'help', label: 'No Priority' };
    }
  };

  // Status configuration helper
  const getStatusConfig = (status) => {
    switch (status) {
      case 'Not Started':
        return { color: 'neutral', icon: 'radio_button_unchecked' };
      case 'In Progress':
        return { color: 'warning', icon: 'pending' };
      case 'Delivered':
        return { color: 'primary', icon: 'local_shipping' };
      case 'Complete':
        return { color: 'success', icon: 'check_circle' };
      case 'On Hold':
        return { color: 'danger', icon: 'pause_circle' };
      default:
        return { color: 'neutral', icon: 'help' };
    }
  };

  // PDF Export Function
  const exportToPDF = async () => {
    setExportingPDF(true);
    
    try {
      const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation for better table fit
      const pageWidth = doc.internal.pageSize.getWidth();
    
    // Add title
    doc.setFontSize(16);
    doc.setTextColor(0, 126, 51); // Success green color
    doc.text('Survey Projects Report', pageWidth / 2, 20, { align: 'center' });
    
    // Add generation date
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, pageWidth / 2, 28, { align: 'center' });
    
    // Prepare table data
    const tableData = [];
    
    // Group projects by county for the PDF
    Object.entries(finalGroupedProjects).forEach(([county, projects]) => {
      // Add county header row
      tableData.push([
        { content: `${county} (${projects.length} projects)`, colSpan: 8, styles: { 
          fillColor: [240, 248, 245], 
          textColor: [0, 126, 51], 
          fontStyle: 'bold',
          halign: 'left'
        }}
      ]);
      
      // Add project rows
      projects.forEach(project => {
        const statusText = project.status || 'Not Started';
        const priorityText = project.priority ? 
          (project.priority === 1 ? 'High' : project.priority === 2 ? 'Medium' : 'Low') : 
          'None';
        const assignedUser = project.assigned_user?.display_name || 'Unassigned';
        const dueDate = project.due_date ? new Date(project.due_date).toLocaleDateString() : 'No due date';
        const deliveredDate = project.date_delivered ? new Date(project.date_delivered).toLocaleDateString() : 'Not delivered';
        
        tableData.push([
          project.project_id || 'N/A',
          project.projects?.name || 'No linked project',
          project.projects?.project_number || 'N/A',
          statusText,
          priorityText,
          assignedUser,
          dueDate,
          deliveredDate
        ]);
      });
    });
    
    // Manual table creation with proper table format
    let currentY = 40;
    const pageHeight = doc.internal.pageSize.getHeight();
    const rowHeight = 8;
    const headerHeight = 10;
    
    // Table column definitions
    const columns = [
      { header: 'Project #', width: 20, x: 15 },
      { header: 'Project Name', width: 50, x: 35 },
      { header: 'Survey Types', width: 35, x: 85 },
      { header: 'Status', width: 25, x: 120 },
      { header: 'Priority', width: 18, x: 145 },
      { header: 'Assigned To', width: 35, x: 163 },
      { header: 'Due Date', width: 25, x: 198 },
      { header: 'Date Delivered', width: 30, x: 223 }
    ];
    
    const tableWidth = columns[columns.length - 1].x + columns[columns.length - 1].width - columns[0].x;
    
    // Helper function to draw table headers
    const drawTableHeaders = (y) => {
      // Header background
      doc.setFillColor(0, 126, 51);
      doc.rect(columns[0].x, y - 2, tableWidth, headerHeight, 'F');
      
      // Header text
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      
      columns.forEach(col => {
        doc.text(col.header, col.x + 2, y + 5);
      });
      
      return y + headerHeight;
    };
    
    // Helper function to draw a table row
    const drawTableRow = (y, data, isEven = false) => {
      // Alternate row background
      if (isEven) {
        doc.setFillColor(248, 250, 252);
        doc.rect(columns[0].x, y, tableWidth, rowHeight, 'F');
      }
      
      // Row border
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.1);
      doc.rect(columns[0].x, y, tableWidth, rowHeight);
      
      // Vertical lines for columns
      columns.forEach((col, index) => {
        if (index > 0) {
          doc.line(col.x, y, col.x, y + rowHeight);
        }
      });
      
      // Row data
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      
      data.forEach((cellData, index) => {
        if (index < columns.length) {
          const col = columns[index];
          const text = String(cellData || '').substring(0, Math.floor(col.width / 2.5)); // Truncate to fit
          doc.text(text, col.x + 2, y + 5);
        }
      });
      
      return y + rowHeight;
    };
    
    // Helper function to draw county header
    const drawCountyHeader = (y, county, projectCount) => {
      doc.setFillColor(240, 248, 245);
      doc.rect(columns[0].x, y, tableWidth, 8, 'F');
      
      doc.setTextColor(0, 126, 51);
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text(`${county} (${projectCount} projects)`, columns[0].x + 5, y + 5);
      
      return y + 8;
    };
    
    Object.entries(finalGroupedProjects).forEach(([county, projects]) => {
      // Check if we need a new page for county header
      if (currentY > pageHeight - 50) {
        doc.addPage();
        currentY = 20;
      }
      
      // Draw county header
      currentY = drawCountyHeader(currentY, county, projects.length);
      currentY += 2;
      
      // Draw table headers
      currentY = drawTableHeaders(currentY);
      
      // Draw project rows
      projects.forEach((project, index) => {
        // Check if we need a new page
        if (currentY > pageHeight - 20) {
          doc.addPage();
          currentY = 20;
          currentY = drawTableHeaders(currentY);
        }
        
        const statusText = project.status || 'Not Started';
        const priorityText = project.priority ? 
          (project.priority === 1 ? 'High' : project.priority === 2 ? 'Medium' : 'Low') : 
          'None';
        const assignedUser = project.assigned_user?.display_name || 'Unassigned';
        const dueDate = project.due_date ? new Date(project.due_date).toLocaleDateString() : '';
        const deliveredDate = project.date_delivered ? new Date(project.date_delivered).toLocaleDateString() : '';
        const surveyTypes = project.survey_types?.map(type => type.type_name).join(', ') || 'None';
        
        const rowData = [
          project.projects?.project_number || 'N/A', // Use project_number instead of project_id
          project.projects?.name || 'No linked project',
          surveyTypes,
          statusText,
          priorityText,
          assignedUser,
          dueDate,
          deliveredDate
        ];
        
        currentY = drawTableRow(currentY, rowData, index % 2 === 0);
      });
      
      currentY += 8; // Space between counties
    });
    
    // Add footer with total count
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Total Projects: ${sortedProjects.length}`, pageWidth / 2, currentY + 10, { align: 'center' });
    
    // Save the PDF
    const fileName = `survey-projects-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <PageLayout title="Survey Tracker">
      <SurveyAnalytics surveyProjects={surveyProjects} />
      
      <Card 
        sx={{ 
          p: 3, 
          mb: 3,
          background: 'linear-gradient(135deg, rgba(0, 168, 132, 0.02) 0%, rgba(0, 126, 51, 0.02) 100%)',
          border: '1px solid',
          borderColor: 'success.200',
        }}
        className="fade-in"
      >
        <Typography 
          level="h4" 
          sx={{ 
            mb: 2, 
            color: 'success.700',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
            tune
          </span>
          Controls & Search
        </Typography>
        
        <Grid container spacing={2} alignItems="flex-end">
          <Grid xs={12} sm="auto">
            <EnhancedButton 
              onClick={() => {
                setFormData({
                  project_id: '',
                  county: '',
                  date_delivered: '',
                  status: 'Not Started',
                  assigned_user_id: '',
                  due_date: '',
                  priority: null,
                  survey_types: [],
                  locations: [{ section: '', township: '', range: '' }]
                });
                setPendingFiles([]);
                setActiveTab(0);
                setOpen(true);
              }} 
              gradient
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                  add_circle
                </span>
              }
            >
              Add New Project
            </EnhancedButton>
          </Grid>
          
          <Grid xs={12} sm={4} md={3}>
            <EnhancedInput
              placeholder="Search by project number, name, county, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '20px', fontFamily: 'Material Symbols Outlined' }}>
                  search
                </span>
              }
            />
          </Grid>
          
          <Grid xs={12} sm={3} md={2}>
            <FormControl sx={{ width: '100%' }}>
              <FormLabel>Filter by County</FormLabel>
              <Select
                value={countyFilter}
                onChange={(e, val) => setCountyFilter(val || '')}
                placeholder="All Counties"
                size="sm"
                sx={{
                  '&:focus-within': {
                    borderColor: 'success.500',
                    boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                  },
                }}
              >
                <Option value="">All Counties</Option>
                <Option value="Pinellas">Pinellas</Option>
                <Option value="Pasco">Pasco</Option>
                <Option value="Hillsborough">Hillsborough</Option>
                <Option value="Hernando">Hernando</Option>
                <Option value="Citrus">Citrus</Option>
                <Option value="Polk">Polk</Option>
                <Option value="Manatee">Manatee</Option>
              </Select>
            </FormControl>
          </Grid>

          <Grid xs={12} sm={3} md={2}>
            <FormControl sx={{ width: '100%' }}>
              <FormLabel>Filter by Status</FormLabel>
              <Select
                value={statusFilter}
                onChange={(e, val) => setStatusFilter(val || '')}
                placeholder="All Statuses"
                size="sm"
                sx={{
                  '&:focus-within': {
                    borderColor: 'success.500',
                    boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                  },
                }}
              >
                <Option value="">All Statuses</Option>
                <Option value="Not Started">Not Started</Option>
                <Option value="In Progress">In Progress</Option>
                <Option value="Delivered">Delivered</Option>
                <Option value="Complete">Complete</Option>
                <Option value="On Hold">On Hold</Option>
              </Select>
            </FormControl>
          </Grid>

          <Grid xs={12} sm={3} md={2}>
            <FormControl sx={{ width: '100%' }}>
              <FormLabel>Filter by Priority</FormLabel>
              <Select
                value={priorityFilter}
                onChange={(e, val) => setPriorityFilter(val || '')}
                placeholder="All Priorities"
                size="sm"
                sx={{
                  '&:focus-within': {
                    borderColor: 'success.500',
                    boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                  },
                }}
              >
                <Option value="">All Priorities</Option>
                <Option value="1">High Priority</Option>
                <Option value="2">Medium Priority</Option>
                <Option value="3">Low Priority</Option>
                <Option value="null">No Priority</Option>
              </Select>
            </FormControl>
          </Grid>

          <Grid xs={12} sm={3} md={2}>
            <FormControl sx={{ width: '100%' }}>
              <FormLabel>Filter by Assignee</FormLabel>
              <Select
                value={assignedUserFilter}
                onChange={(e, val) => setAssignedUserFilter(val || '')}
                placeholder="All Assignees"
                size="sm"
                sx={{
                  '&:focus-within': {
                    borderColor: 'success.500',
                    boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                  },
                }}
              >
                <Option value="">All Assignees</Option>
                <Option value="unassigned">Unassigned</Option>
                {users && users.map((user) => (
                  <Option key={user.id} value={user.id}>
                    {user.display_name || user.email}
                  </Option>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          {/* Quick Filter Chips - Same Row */}
          <Grid xs={12} sm="auto" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <Chip
              variant={quickFilters.hasFiles ? 'solid' : 'outlined'}
              color={quickFilters.hasFiles ? 'success' : 'neutral'}
              onClick={() => toggleQuickFilter('hasFiles')}
              sx={{ cursor: 'pointer' }}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                  attach_file
                </span>
              }
            >
              Has Files
            </Chip>
            
            <Chip
              variant={quickFilters.hasComments ? 'solid' : 'outlined'}
              color={quickFilters.hasComments ? 'success' : 'neutral'}
              onClick={() => toggleQuickFilter('hasComments')}
              sx={{ cursor: 'pointer' }}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                  comment
                </span>
              }
            >
              Has Comments
            </Chip>
            
            <Chip
              variant={quickFilters.deliveredThisMonth ? 'solid' : 'outlined'}
              color={quickFilters.deliveredThisMonth ? 'success' : 'neutral'}
              onClick={() => toggleQuickFilter('deliveredThisMonth')}
              sx={{ cursor: 'pointer' }}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                  calendar_month
                </span>
              }
            >
              Delivered This Month
            </Chip>
            
            <Chip
              variant={quickFilters.isOverdue ? 'solid' : 'outlined'}
              color={quickFilters.isOverdue ? 'danger' : 'neutral'}
              onClick={() => toggleQuickFilter('isOverdue')}
              sx={{ cursor: 'pointer' }}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                  schedule
                </span>
              }
            >
              Overdue
            </Chip>
            
            <Chip
              variant={quickFilters.isHighPriority ? 'solid' : 'outlined'}
              color={quickFilters.isHighPriority ? 'danger' : 'neutral'}
              onClick={() => toggleQuickFilter('isHighPriority')}
              sx={{ cursor: 'pointer' }}
              startDecorator={
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                  priority_high
                </span>
              }
            >
              High Priority
            </Chip>
            
            {getActiveFilterCount() > 0 && (
              <Button
                variant="outlined"
                size="sm"
                color="neutral"
                onClick={() => setQuickFilters({
                  hasFiles: false,
                  hasComments: false,
                  deliveredThisMonth: false,
                  isOverdue: false,
                  isHighPriority: false
                })}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                    clear
                  </span>
                }
                sx={{ ml: 1 }}
              >
                Clear Filters ({getActiveFilterCount()})
              </Button>
            )}
            
            {/* Export to PDF Button */}
            <EnhancedButton
              variant="outlined"
              color="success"
              size="sm"
              onClick={exportToPDF}
              loading={exportingPDF}
              disabled={exportingPDF}
              startDecorator={
                !exportingPDF && (
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                    picture_as_pdf
                  </span>
                )
              }
              sx={{ ml: 1 }}
            >
              {exportingPDF ? 'Generating PDF...' : 'Export PDF'}
            </EnhancedButton>
          </Grid>
        </Grid>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog 
          sx={{ 
            maxWidth: '900px', 
            maxHeight: '90vh', 
            overflow: 'auto',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: 'success.200',
          }}
        >
          <ModalClose />
          <Typography 
            level="h4" 
            sx={{ 
              mb: 3,
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '28px', fontFamily: 'Material Symbols Outlined' }}>
              {formData.id ? 'edit' : 'add_circle'}
            </span>
            {formData.id ? 'Edit Survey Project' : 'Add New Survey Project'}
          </Typography>

          <Tabs value={activeTab} onChange={(event, newValue) => setActiveTab(newValue)}>
            <TabList>
              <Tab>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', marginRight: '8px' }}>
                  assignment
                </span>
                Project Details
              </Tab>
              <Tab>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', marginRight: '8px' }}>
                  attach_file
                </span>
                Files ({formData.id ? projectFiles.totalFiles : pendingFiles.length})
              </Tab>
            </TabList>

            <TabPanel value={0} sx={{ px: 0 }}>
          
          <Grid container spacing={2}>
            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>Project</FormLabel>
                <Select
                  value={formData.project_id}
                  onChange={(e, val) => setFormData({ ...formData, project_id: val })}
                  required
                  sx={{
                    '&:focus-within': {
                      borderColor: 'success.500',
                      boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                    },
                  }}
                >
                  {allProjects && allProjects.map((project) => (
                    <Option key={project.id} value={project.id}>
                      {project.project_number ? `${project.project_number} - ${project.name}` : project.name}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>County</FormLabel>
                <Select
                  value={formData.county}
                  onChange={(e, val) => setFormData({ ...formData, county: val })}
                  required
                  sx={{
                    '&:focus-within': {
                      borderColor: 'success.500',
                      boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                    },
                  }}
                >
                  <Option value="Pinellas">Pinellas</Option>
                  <Option value="Pasco">Pasco</Option>
                  <Option value="Hillsborough">Hillsborough</Option>
                  <Option value="Hernando">Hernando</Option>
                  <Option value="Citrus">Citrus</Option>
                  <Option value="Polk">Polk</Option>
                  <Option value="Manatee">Manatee</Option>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label="Date Delivered"
                type="date"
                value={formData.date_delivered}
                onChange={(e) => setFormData({ ...formData, date_delivered: e.target.value })}
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>Project Status</FormLabel>
                <Select
                  value={formData.status}
                  onChange={(e, val) => setFormData({ ...formData, status: val })}
                  required
                  renderValue={(option) => {
                    if (!option) return null;
                    const value = option.value || option;
                    let icon, text;
                    
                    switch (value) {
                      case 'Not Started':
                        icon = 'radio_button_unchecked';
                        text = 'Not Started';
                        break;
                      case 'In Progress':
                        icon = 'pending';
                        text = 'In Progress';
                        break;
                      case 'Delivered':
                        icon = 'local_shipping';
                        text = 'Delivered';
                        break;
                      case 'Complete':
                        icon = 'check_circle';
                        text = 'Complete';
                        break;
                      case 'On Hold':
                        icon = 'pause_circle';
                        text = 'On Hold';
                        break;
                      default:
                        icon = 'radio_button_unchecked';
                        text = value;
                    }
                    
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                          {icon}
                        </span>
                        {text}
                      </Box>
                    );
                  }}
                  sx={{
                    '&:focus-within': {
                      borderColor: 'success.500',
                      boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                    },
                  }}
                >
                  <Option value="Not Started">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                        radio_button_unchecked
                      </span>
                      Not Started
                    </Box>
                  </Option>
                  <Option value="In Progress">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                        pending
                      </span>
                      In Progress
                    </Box>
                  </Option>
                  <Option value="Delivered">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                        local_shipping
                      </span>
                      Delivered
                    </Box>
                  </Option>
                  <Option value="Complete">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                        check_circle
                      </span>
                      Complete
                    </Box>
                  </Option>
                  <Option value="On Hold">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                        pause_circle
                      </span>
                      On Hold
                    </Box>
                  </Option>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>Assigned Surveyor</FormLabel>
                <Select
                  value={formData.assigned_user_id}
                  onChange={(e, val) => setFormData({ ...formData, assigned_user_id: val })}
                  placeholder="Select surveyor..."
                  sx={{
                    '&:focus-within': {
                      borderColor: 'success.500',
                      boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                    },
                  }}
                >
                  <Option value="">Unassigned</Option>
                  {users && users.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.display_name || user.email}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid xs={12} sm={6}>
              <EnhancedInput
                label="Due Date"
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              />
            </Grid>
            
            <Grid xs={12} sm={6}>
              <FormControl>
                <FormLabel>Priority</FormLabel>
                <Select
                  value={formData.priority}
                  onChange={(e, val) => setFormData({ ...formData, priority: val })}
                  placeholder="Select priority..."
                  renderValue={(option) => {
                    if (option === null || option === undefined) return "No Priority";
                    const value = option.value !== undefined ? option.value : option;
                    let icon, text;
                    
                    switch (value) {
                      case 1:
                        icon = 'priority_high';
                        text = 'High Priority';
                        break;
                      case 2:
                        icon = 'drag_handle';
                        text = 'Medium Priority';
                        break;
                      case 3:
                        icon = 'low_priority';
                        text = 'Low Priority';
                        break;
                      default:
                        return "No Priority";
                    }
                    
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                          {icon}
                        </span>
                        {text}
                      </Box>
                    );
                  }}
                  sx={{
                    '&:focus-within': {
                      borderColor: 'success.500',
                      boxShadow: '0 0 0 2px rgba(0, 168, 132, 0.25)',
                    },
                  }}
                >
                  <Option value={null}>No Priority</Option>
                  <Option value={1}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '16px', color: '#d32f2f' }}>
                        priority_high
                      </span>
                      High Priority
                    </Box>
                  </Option>
                  <Option value={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '16px', color: '#ed6c02' }}>
                        drag_handle
                      </span>
                      Medium Priority
                    </Box>
                  </Option>
                  <Option value={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span className="material-symbols-outlined" style={{ fontSize: '16px', color: '#2e7d32' }}>
                        low_priority
                      </span>
                      Low Priority
                    </Box>
                  </Option>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid xs={12}>
              <FormControl>
                <FormLabel>Survey Types</FormLabel>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                  {surveyTypes.map((type) => (
                    <Button
                      key={type.id}
                      size="sm"
                      variant={formData.survey_types.includes(type.id) ? 'solid' : 'outlined'}
                      color={formData.survey_types.includes(type.id) ? 'success' : 'neutral'}
                      onClick={() => {
                        const newTypes = formData.survey_types.includes(type.id)
                          ? formData.survey_types.filter(id => id !== type.id)
                          : [...formData.survey_types, type.id];
                        setFormData({ ...formData, survey_types: newTypes });
                      }}
                      sx={{
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        },
                      }}
                    >
                      {type.type_name}
                    </Button>
                  ))}
                </Box>
              </FormControl>
            </Grid>
            
            <Grid xs={12}>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Section/Township/Range Locations
              </Typography>
              {formData.locations.map((location, index) => (
                <Box key={`location-${index}`} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-end' }}>
                  <EnhancedInput
                    placeholder="Section"
                    value={location.section || ''}
                    onChange={(e) => updateLocation(index, 'section', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <EnhancedInput
                    placeholder="Township"
                    value={location.township || ''}
                    onChange={(e) => updateLocation(index, 'township', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <EnhancedInput
                    placeholder="Range"
                    value={location.range || ''}
                    onChange={(e) => updateLocation(index, 'range', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  {formData.locations.length > 1 && (
                    <IconButton 
                      size="sm" 
                      color="danger" 
                      onClick={() => removeLocation(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
              <EnhancedButton
                variant="outlined"
                size="sm"
                onClick={addLocation}
                startDecorator={
                  <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                    add
                  </span>
                }
              >
                Add Location
              </EnhancedButton>
            </Grid>
            
            <Grid xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                <EnhancedButton
                  variant="outlined"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </EnhancedButton>
                <EnhancedButton
                  gradient
                  onClick={handleSubmit}
                >
                  {formData.id ? 'Update' : 'Create'}
                </EnhancedButton>
              </Box>
            </Grid>
          </Grid>
          </TabPanel>

            <TabPanel value={1} sx={{ px: 0 }}>
              <Grid container spacing={3}>
                <Grid xs={12}>
                  <Typography level="h4" sx={{ mb: 2 }}>
                    {formData.id ? 'Upload Files' : 'Select Files to Upload'}
                  </Typography>
                  
                  {formData.id ? (
                    // Existing project - direct upload
                    <FileUpload
                      surveyProjectId={formData.id}
                      userId={user?.id}
                      onFilesUploaded={handleFilesUploaded}
                      acceptedTypes="documents,photos"
                    />
                  ) : (
                    // New project - drag and drop file selection
                    <Box>
                      <Typography level="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Select files to upload after the project is created. Files will be uploaded automatically once you save the project.
                      </Typography>
                      
                      {/* Drag and Drop Area */}
                      <Card
                        variant="outlined"
                        sx={{
                          p: 3,
                          border: dragOver ? '2px dashed' : '2px dashed',
                          borderColor: dragOver ? 'success.500' : 'neutral.300',
                          backgroundColor: dragOver ? 'success.50' : 'background.body',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: 'success.400',
                            backgroundColor: 'success.25'
                          }
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          setDragOver(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setDragOver(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragOver(false);
                          const files = Array.from(e.dataTransfer.files || []);
                          setPendingFiles(prev => [...prev, ...files]);
                        }}
                        onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file';
                          input.multiple = true;
                          input.accept = '.pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.heic,.webp';
                          input.onchange = (e) => {
                            const files = Array.from(e.target.files || []);
                            setPendingFiles(prev => [...prev, ...files]);
                          };
                          input.click();
                        }}
                      >
                        <Box sx={{ textAlign: 'center' }}>
                          <CloudUploadIcon sx={{ fontSize: 48, color: 'success.500', mb: 1 }} />
                          <Typography level="h4" sx={{ mb: 1 }}>
                            Upload Documents & Photos
                          </Typography>
                          <Typography level="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                            Drag and drop files here or click to browse
                          </Typography>
                          <Typography level="body3" sx={{ color: 'text.tertiary' }}>
                            Documents: PDF, DOC, DOCX, XLS, XLSX, TXT (max 50MB) → survey-documents bucket
                            • Photos: JPG, PNG, HEIC, WEBP (max 25MB) → survey-photos bucket
                          </Typography>
                        </Box>
                      </Card>
                      
                      {/* Selected Files Preview */}
                      {pendingFiles.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          <Typography level="title-sm" sx={{ mb: 1 }}>
                            Selected Files ({pendingFiles.length})
                          </Typography>
                          <Grid container spacing={1}>
                            {pendingFiles.map((file, index) => (
                              <Grid xs={12} key={index}>
                                <Card variant="outlined" sx={{ p: 2 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {file.type.startsWith('image/') ? (
                                      <PhotoIcon sx={{ color: 'primary.500' }} />
                                    ) : (
                                      <InsertDriveFileIcon sx={{ color: 'neutral.500' }} />
                                    )}
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                      <Typography level="body2" noWrap>
                                        {file.name}
                                      </Typography>
                                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                                        <Chip 
                                          size="sm" 
                                          variant="soft" 
                                          color={file.type.startsWith('image/') ? 'primary' : 'neutral'}
                                        >
                                          {file.type.startsWith('image/') ? 'Photo' : 'Document'}
                                        </Chip>
                                        <Typography level="body3" sx={{ color: 'text.secondary' }}>
                                          {(file.size / 1024 / 1024).toFixed(2)} MB • {file.type.startsWith('image/') ? 'survey-photos' : 'survey-documents'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    <IconButton
                                      size="sm"
                                      color="danger"
                                      onClick={() => {
                                        setPendingFiles(prev => prev.filter((_, i) => i !== index));
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                          
                          {/* Clear All Button */}
                          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                              variant="outlined"
                              size="sm"
                              onClick={() => setPendingFiles([])}
                            >
                              Clear All
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Grid>
                
                {formData.id && (
                  <Grid xs={12} sx={{ mt: 2 }}>
                    <FileManager
                      surveyProjectId={formData.id}
                      onFilesChange={handleFilesChanged}
                    />
                  </Grid>
                )}
              </Grid>
            </TabPanel>
          </Tabs>
        </ModalDialog>
      </Modal>

      <Modal open={commentModalOpen} onClose={() => setCommentModalOpen(false)}>
        <ModalDialog 
          sx={{ 
            maxWidth: '600px', 
            maxHeight: '80vh',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
            backdropFilter: 'blur(15px)',
            border: '1px solid',
            borderColor: 'success.200',
          }}
        >
          <ModalClose />
          <Typography 
            level="h4" 
            sx={{ 
              mb: 2,
              color: 'success.700',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '24px', fontFamily: 'Material Symbols Outlined' }}>
              comment
            </span>
            Comments - {selectedProject?.project_name}
          </Typography>
          
          <Box sx={{ maxHeight: '300px', overflow: 'auto', mb: 2 }}>
            {comments.length === 0 ? (
              <Typography level="body2" sx={{ textAlign: 'center', py: 2 }}>
                No comments yet
              </Typography>
            ) : (
              comments.map((comment) => (
                <Card key={comment.id} sx={{ mb: 1, p: 2 }}>
                  <Typography level="body2" sx={{ mb: 1 }}>
                    {comment.comment_text}
                  </Typography>
                  <Typography level="body3" sx={{ color: 'text.tertiary' }}>
                    {comment.user_name} • {new Date(comment.created_at).toLocaleDateString()}
                  </Typography>
                </Card>
              ))
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <EnhancedInput
              placeholder="Add a comment..."
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              sx={{ flexGrow: 1 }}
            />
            <EnhancedButton onClick={handleAddComment}>
              Add
            </EnhancedButton>
          </Box>
        </ModalDialog>
      </Modal>

      {totalItems > 0 ? (
        <Box sx={{ mt: 3 }}>
          {Object.entries(finalGroupedProjects).map(([county, projects]) => {
            const allCountyProjects = allGroupedProjects[county] || [];
            const isExpanded = expandedCounties.has(county);
            const hasMoreProjects = allCountyProjects.length > projectsPerCounty;
            
            return (
            <Box key={county} sx={{ mb: 4 }}>
              {/* County Header */}
              <Box 
                sx={{ 
                  mb: 2, 
                  p: 2, 
                  backgroundColor: 'success.50',
                  borderRadius: 'md',
                  border: '1px solid',
                  borderColor: 'success.200',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <span className="material-symbols-outlined" style={{ fontSize: '24px', color: 'var(--joy-palette-success-600)' }}>
                  location_on
                </span>
                <Typography 
                  level="h4" 
                  sx={{ 
                    color: 'success.700',
                    fontWeight: 600
                  }}
                >
                  {county}
                </Typography>
                <Chip 
                  variant="soft" 
                  color="success" 
                  size="sm"
                >
                  {projects.length} project{projects.length !== 1 ? 's' : ''}
                  {hasMoreProjects && !isExpanded && ` (showing ${projectsPerCounty} of ${allCountyProjects.length})`}
                </Chip>
                
                {hasMoreProjects && (
                  <EnhancedButton
                    size="sm"
                    variant="outlined"
                    onClick={() => toggleCountyExpansion(county)}
                    startDecorator={
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                        {isExpanded ? 'expand_less' : 'expand_more'}
                      </span>
                    }
                  >
                    {isExpanded ? 'Show Less' : `Show All ${allCountyProjects.length}`}
                  </EnhancedButton>
                )}
              </Box>

              {/* County Projects Table */}
              <StyledTable
                columns={[
                  { key: 'project_number', label: 'Project Number' },
                  { key: 'project_name', label: 'Project Name' },
                  { key: 'status', label: 'Status' },
                  { key: 'assigned_user', label: 'Assigned To' },
                  { key: 'priority', label: 'Priority' },
                  { key: 'due_date', label: 'Due Date' },
                  { key: 'survey_types_display', label: 'Types' },
                  { key: 'locations_text', label: 'Locations' },
                  { key: 'date_delivered', label: 'Date Delivered' },
                  { key: 'files_count', label: 'Files' },
                  { key: 'comment_count', label: 'Comments' },
                  { key: 'actions', label: 'Actions' }
                ]}
                rows={projects.map((project) => ({
                  project_number: project.project_number,
                  project_name: project.project_name,
                  status: (
                    <Chip 
                      variant="soft" 
                      size="sm" 
                      color={getStatusConfig(project.status).color}
                      startDecorator={
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>
                          {getStatusConfig(project.status).icon}
                        </span>
                      }
                    >
                      {project.status || 'Not Started'}
                    </Chip>
                  ),
                  assigned_user: project.assigned_user_name || (
                    <Typography level="body3" sx={{ color: 'text.secondary' }}>
                      Unassigned
                    </Typography>
                  ),
                  priority: project.priority ? (
                    <Chip 
                      variant="soft" 
                      size="sm" 
                      color={getPriorityConfig(project.priority).color}
                      startDecorator={
                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>
                          {getPriorityConfig(project.priority).icon}
                        </span>
                      }
                    >
                      {getPriorityConfig(project.priority).label.replace(' Priority', '')}
                    </Chip>
                  ) : (
                    <Typography level="body3" sx={{ color: 'text.secondary' }}>
                      No Priority
                    </Typography>
                  ),
                  due_date: project.due_date ? (
                    <Box>
                      <Typography level="body2">
                        {new Date(project.due_date).toLocaleDateString()}
                      </Typography>
                      {project.due_date && new Date(project.due_date) < new Date() && (
                        <Typography level="body3" sx={{ color: 'danger.500' }}>
                          Overdue
                        </Typography>
                      )}
                    </Box>
                  ) : '—',
                  survey_types_display: (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {project.survey_types.map((type) => (
                        <Chip key={type.id} variant="soft" size="sm">
                          {type.type_name}
                        </Chip>
                      ))}
                    </Box>
                  ),
                  locations_text: project.locations_text,
                  date_delivered: project.date_delivered 
                    ? new Date(project.date_delivered).toLocaleDateString() 
                    : '—',
                  files_display: (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {project.file_counts.documents > 0 && (
                        <Chip 
                          size="sm" 
                          variant="soft" 
                          color="neutral"
                          startDecorator={
                            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>
                              description
                            </span>
                          }
                        >
                          {project.file_counts.documents}
                        </Chip>
                      )}
                      {project.file_counts.photos > 0 && (
                        <Chip 
                          size="sm" 
                          variant="soft" 
                          color="primary"
                          startDecorator={
                            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>
                              photo
                            </span>
                          }
                        >
                          {project.file_counts.photos}
                        </Chip>
                      )}
                      {project.file_counts.documents === 0 && project.file_counts.photos === 0 && (
                        <Typography level="body3" sx={{ color: 'text.secondary' }}>
                          —
                        </Typography>
                      )}
                    </Box>
                  ),
                  files_count: (
                    <EnhancedButton
                      size="sm"
                      variant="outlined"
                      onClick={() => handleViewFiles(project)}
                      startDecorator={
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                          attach_file
                        </span>
                      }
                    >
                      {project.file_counts.documents + project.file_counts.photos}
                    </EnhancedButton>
                  ),
                  comment_count: (
                    <EnhancedButton
                      size="sm"
                      variant="outlined"
                      onClick={() => handleViewComments(project)}
                      startDecorator={
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                          comment
                        </span>
                      }
                    >
                      {project.comment_count}
                    </EnhancedButton>
                  ),
                  actions: (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton size="sm" color="neutral" onClick={() => handleEdit(project)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="sm" color="danger" onClick={() => handleDelete(project)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  )
                }))}
                onSortChange={onSortChange}
                sortState={{ sortKey, sortOrder }}
              />
            </Box>
          );
          })}
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Card sx={{ 
              p: 2, 
              mt: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              background: 'linear-gradient(135deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.95) 100%)',
              border: '1px solid',
              borderColor: 'neutral.200',
            }}>
              <Typography level="body-sm" sx={{ color: 'neutral.700' }}>
                Showing {(currentPage - 1) * maxCountiesPerPage + 1}-{Math.min(currentPage * maxCountiesPerPage, totalCounties)} of {totalCounties} counties ({totalItems} total projects)
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  startDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                      chevron_left
                    </span>
                  }
                >
                  Previous
                </Button>
                
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    const isCurrentPage = page === currentPage;
                    const showPage = page === 1 || page === totalPages || 
                      (page >= currentPage - 2 && page <= currentPage + 2);
                    
                    if (!showPage && page === 2 && currentPage > 4) {
                      return <Typography key="ellipsis1" level="body-sm" sx={{ px: 1, color: 'neutral.500' }}>...</Typography>;
                    }
                    if (!showPage && page === totalPages - 1 && currentPage < totalPages - 3) {
                      return <Typography key="ellipsis2" level="body-sm" sx={{ px: 1, color: 'neutral.500' }}>...</Typography>;
                    }
                    if (!showPage) return null;
                    
                    return (
                      <Button
                        key={page}
                        variant={isCurrentPage ? 'solid' : 'outlined'}
                        color={isCurrentPage ? 'success' : 'neutral'}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        sx={{ minWidth: '36px' }}
                      >
                        {page}
                      </Button>
                    );
                  })}
                </Box>
                
                <Button
                  variant="outlined"
                  size="sm"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  endDecorator={
                    <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                      chevron_right
                    </span>
                  }
                >
                  Next
                </Button>
              </Box>
            </Card>
          )}
        </Box>
      ) : (
        <Typography level="body2" sx={{ mt: 4, textAlign: 'center' }}>
          No survey projects found.
        </Typography>
      )}
    </PageLayout>
  );
};

export default SurveyTracker;